import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import apiService from '../services/apiService';
import envConfig from '../config/envConfig';
import { MiniCalendar, Modal, Stars, Badge, Avatar } from '../components/UI';
import { Star, BookOpen, Clock, Video, ArrowLeft, CheckCircle, Info, Award, Play, Linkedin, Globe } from 'lucide-react';

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function ExpertProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, experts, addBookingToState, toast } = useApp();

  const expert = experts.find(p => String(p.id) === id);
  const isOwnProfile = user && String(user.id) === String(expert?.id);
  const canBook = user && (user.role === 'learner' || user.role === 'receiver') && !isOwnProfile;

  const [calDate, setCalDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingMsg, setBookingMsg] = useState('');
  const [booked, setBooked] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [paymentStep, setPaymentStep] = useState('idle'); // 'idle' | 'ordering' | 'modal' | 'confirming' | 'success'
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [slotTypeFilter, setSlotTypeFilter] = useState('1:1'); // '1:1' | 'group'
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [enrollingCourse, setEnrollingCourse] = useState(false);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [activePack, setActivePack] = useState(null); // learner's active pack for this expert
  const [activePackLoading, setActivePackLoading] = useState(false);
  const [showPackBuyModal, setShowPackBuyModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [buyingPack, setBuyingPack] = useState(false);
  const [checkingSlotId, setCheckingSlotId] = useState(null); // tracks which Book button is loading
  const [showDateSlots, setShowDateSlots] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showCredsModal, setShowCredsModal] = useState(false);
  const [showLangsModal, setShowLangsModal] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!id) return;
    setSlotsLoading(true);
    apiService.getAvailableSlots(id)
      .then(res => { if (res.success) setAvailableSlots(res.data); })
      .catch(() => {})
      .finally(() => setSlotsLoading(false));
    setCoursesLoading(true);
    apiService.getCoursesForExpert(id)
      .then(res => { if (res.success) setCourses(res.data); })
      .catch(() => {})
      .finally(() => setCoursesLoading(false));
    setPacksLoading(true);
    apiService.getPacksForExpert(id)
      .then(res => { if (res.success) setPacks(res.data); })
      .catch(() => {})
      .finally(() => setPacksLoading(false));
  }, [id]);

  // Check if learner already has an active pack for this expert
  useEffect(() => {
    if (!user || !id || (user.role !== 'learner' && user.role !== 'receiver')) return;
    setActivePackLoading(true);
    apiService.getActivePackForExpert(id)
      .then(res => { if (res.success) setActivePack(res.data); })
      .catch(() => {})
      .finally(() => setActivePackLoading(false));
  }, [id, user?.id]);

  if (experts.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)' }}>Loading…</div>;
  }

  if (!expert) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Expert not found.</div>;
  }

  const handleEnrollCourse = async () => {
    if (!user) { navigate('/auth?mode=login'); toast('Please log in to enroll'); return; }
    if (!selectedCourse) return;
    setEnrollingCourse(true);
    try {
      const res = await apiService.enrollCourse(selectedCourse.id);
      if (res.success) {
        setEnrolledCourseIds(prev => [...prev, selectedCourse.id]);
        setShowCourseModal(false);
        toast('Enrolled successfully! Check your schedule. ✓', 'success');
      } else { toast(res.message || 'Enrollment failed', 'error'); }
    } catch (err) { toast(err.message || 'Enrollment failed', 'error'); }
    finally { setEnrollingCourse(false); }
  };

  const handleBuyPack = async () => {
    if (!user) { navigate('/auth?mode=login'); toast('Please log in to buy a pack'); return; }
    if (!selectedPack) return;
    setBuyingPack(true);
    try {
      const res = await apiService.buyPack(selectedPack.id);
      if (res.success) {
        setActivePack(res.data);
        setShowPackBuyModal(false);
        setSelectedPack(null);
        toast(`Pack purchased! You have ${res.data.sessions_total} sessions to use. ✓`, 'success');
      } else { toast(res.message || 'Purchase failed', 'error'); }
    } catch (err) { toast(err.message || 'Purchase failed', 'error'); }
    finally { setBuyingPack(false); }
  };

  const slotPrices = availableSlots.map(s => Number(s.price)).filter(n => n > 0);
  const minSlotPrice = slotPrices.length > 0 ? Math.min(...slotPrices) : (expert.rate || 0);

  const slotsForDay = (d) => {
    const ds = `${calDate.getFullYear()}-${String(calDate.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return availableSlots.filter(s => {
      const dateMatch = s.date === ds;
      const typeMatch = (slotTypeFilter === '1:1' && s.type !== 'group') || (slotTypeFilter === 'group' && s.type === 'group');
      return dateMatch && typeMatch;
    });
  };

  // Called when learner clicks the "Book" pill — checks availability before opening modal
  const handleBookClick = async (e, slot) => {
    e.stopPropagation();
    if (!user) { navigate('/auth?mode=login'); toast('Please log in to book a session', 'error'); return; }
    if (user.role !== 'learner' && user.role !== 'receiver') {
      toast('Only learners can book sessions', 'error');
      return;
    }
    setCheckingSlotId(slot.id);
    try {
      const fresh = await apiService.getAvailableSlots(expert.id);
      if (!fresh.success) throw new Error('Failed to check availability');
      const freshList = fresh.data || [];
      setAvailableSlots(freshList);
      const still = freshList.find(s => s.id === slot.id);
      if (!still) {
        toast('This slot is no longer available. Please pick another.', 'error');
        setSelectedSlot(null);
        return;
      }
      setSelectedSlot(still);
      setShowBooking(true);
    } catch (err) {
      toast('Could not verify slot availability. Please try again.', 'error');
    } finally {
      setCheckingSlotId(null);
    }
  };

  const handleBook = async () => {
    if (!user) { navigate('/auth?mode=login'); toast('Please log in to book a session'); return; }
    if (!selectedSlot?.id) { toast('Please select a time slot first', 'error'); return; }
    setBookingLoading(true);

    try {
      // If learner has active pack with remaining sessions, use pack credit
      if (activePack && activePack.sessions_used < activePack.sessions_total) {
        const res = await apiService.bookWithPackCredit(selectedSlot.id, bookingMsg);
        if (res.success) {
          addBookingToState(res.data);
          setShowBooking(false);
          setBookingMsg('');
          setSelectedSlot(null);
          setBooked(true);
          toast('Booking confirmed using pack credit! ✓', 'success');
          setAvailableSlots(prev => prev.filter(s => s.id !== selectedSlot.id));
          const packRes = await apiService.getActivePackForExpert(expert.id);
          if (packRes.success) setActivePack(packRes.data);
        } else {
          toast(res.message || 'Booking failed', 'error');
        }
        return;
      }

      // Create Cashfree payment order
      setPaymentStep('ordering');
      const res = await apiService.createPaymentOrder({ slot_id: selectedSlot.id });
      if (!res.success) {
        toast(res.message || 'Failed to create payment order', 'error');
        setPaymentStep('idle');
        return;
      }

      const { paymentSessionId } = res.data;

      // Save booking message so PaymentReturn can use it after redirect
      sessionStorage.setItem('cf_booking_msg', bookingMsg);

      // Load Cashfree JS SDK and launch checkout (redirects to Cashfree payment page)
      const { load } = await import('@cashfreepayments/cashfree-js');
      const cashfree = await load({ mode: envConfig.isProduction ? 'sandbox' : 'sandbox' });
      cashfree.checkout({
        paymentSessionId,
        returnUrl: `${envConfig.frontendUrl}/payment-return?order_id={order_id}`,
      });
      // Page will redirect — no code runs after this
    } catch (err) {
      toast(err.message || 'Booking failed', 'error');
      setPaymentStep('idle');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <div className="expert-profile-hero" style={{ background: `linear-gradient(135deg, ${expert.bg || '#0B6E65'} 0%, ${expert.bg || '#0B6E65'}CC 100%)`, padding: '20px 0 56px' }}>
        <div className="container">
          <button onClick={() => navigate(-1)} className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', gap: 6, marginBottom: 20 }}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </div>

      <div className="container" style={{ marginTop: -48, paddingBottom: 40 }}>
        
        {/* Booked confirmation */}
        {booked && (
          <div style={{ background: 'var(--teal-lt)', border: '1px solid var(--teal)', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <CheckCircle size={22} style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 4 }}>Booking request sent!</div>
              <div style={{ fontSize: 13, color: 'var(--ink3)' }}>Your request is pending confirmation from {expert.name}. You'll be notified once they accept and a video link is generated.</div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: 20, alignItems: 'start' }}>

          {/* ── Left card ── */}
          <div className="card" style={{ padding: '20px' }}>

            {/* Profile row */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
              <Avatar name={expert.name} bg={expert.bg} src={expert.profile_image} size="lg"
                style={{ border: '3px solid white', boxShadow: 'var(--shadow)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{expert.name}</h1>
                  {expert.rating > 0
                    ? <span style={{ fontSize: 12, color: 'var(--ink3)' }}><Stars rating={expert.rating} /> {expert.rating}</span>
                    : <span className="badge" style={{ fontSize: 10, background: 'var(--bg2)', color: 'var(--ink4)' }}>New Expert</span>
                  }
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>
                  {expert.catName || expert.category_name || ''} Expert
                </div>
                {(expert.subcategories || []).length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 3 }}>
                    {(expert.subcategories || []).slice(0, 4).join(' | ')}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                  {(expert.tags || []).slice(0, 4).map(t => <span key={t} className="badge badge-teal" style={{ fontSize: 10, padding: '2px 6px' }}>{t}</span>)}
                </div>
              </div>
            </div>

            <hr className="divider" style={{ margin: '12px 0' }} />

            {/* Mobile info pills */}
            {isMobile && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 12, scrollbarWidth: 'none' }}>
                <button onClick={() => setShowAboutModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1.5px solid var(--border2)', background: 'var(--surface)', color: 'var(--ink2)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  <Info size={13} style={{ color: 'var(--teal)' }} /> About
                </button>
                <button onClick={() => setShowCredsModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1.5px solid var(--border2)', background: 'var(--surface)', color: 'var(--ink2)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  <Award size={13} style={{ color: 'var(--saffron)' }} /> Credentials
                </button>
                {expert.languages && (
                  <button onClick={() => setShowLangsModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1.5px solid var(--border2)', background: 'var(--surface)', color: 'var(--ink2)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    🌐 Languages
                  </button>
                )}
              </div>
            )}

            {/* Session type toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>Session type:</span>
              {[{ value: '1:1', label: '👤 1:1 Session' }, { value: 'group', label: '🎓 Group Class' }].map(opt => (
                <button key={opt.value} onClick={() => setSlotTypeFilter(opt.value)} style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  border: `1.5px solid ${slotTypeFilter === opt.value ? 'var(--teal)' : 'var(--border2)'}`,
                  background: slotTypeFilter === opt.value ? '#E3F4F2' : 'transparent',
                  color: slotTypeFilter === opt.value ? '#0B6E65' : 'var(--ink3)',
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap'
                }}>{opt.label}</button>
              ))}
            </div>

            {/* Calendar — click a date to see slots */}
            {slotsLoading
              ? <p style={{ fontSize: 12, color: 'var(--ink4)' }}>Loading slots…</p>
              : (
                <>
                  <div className="cal-compact">
                    <MiniCalendar
                      year={calDate.getFullYear()} month={calDate.getMonth()}
                      onNavigate={d => setCalDate(prev => { const n = new Date(prev); n.setMonth(n.getMonth() + d); return n; })}
                      onSelectDay={d => { setSelectedDate(d); setSelectedSlot(null); setShowDateSlots(true); }}
                      dimEmpty
                      renderDay={d => {
                        const sl = slotsForDay(d);
                        const openSlots = sl.filter(s => !s.booked);
                        const takenSlots = sl.filter(s => s.booked);
                        const dots = [
                          ...openSlots.slice(0, 3).map(s => <span key={`o-${s.id}`} className="cal-dot cal-dot-open" />),
                          ...(takenSlots.length > 0 ? [<span key="t" className="cal-dot cal-dot-taken" />] : []),
                        ];
                        return {
                          hasSlots: sl.length > 0,
                          selected: selectedDate === d,
                          dots: <div style={{ lineHeight: 1 }}>{dots}</div>
                        };
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--ink4)', marginTop: 10, justifyContent: 'center', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="cal-dot cal-dot-open" style={{ display: 'inline-block', flexShrink: 0 }} />
                      Available
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="cal-dot cal-dot-taken" style={{ display: 'inline-block', flexShrink: 0 }} />
                      Booked
                    </span>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 6, textAlign: 'center' }}>
                    <Clock size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                    Tap a date to see slots
                  </p>
                </>
              )}

            {/* Session Packs — Full Width Below */}
            {(packsLoading || packs.length > 0) && (
              <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border2)' }}>

                {/* Active pack banner */}
                {activePack && (
                  <div style={{ background: activePack.type === 'group' ? '#FDF1E3' : '#E3F4F2', border: `1.5px solid ${activePack.type === 'group' ? '#D4720C' : 'var(--teal)'}`, borderRadius:10, padding:'12px 16px', marginBottom:20, display:'flex', gap:12, alignItems:'center' }}>
                    <span style={{ fontSize:22 }}>✅</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, color: activePack.type === 'group' ? '#D4720C' : 'var(--teal)', fontSize:14 }}>{activePack.title} — Active {activePack.type === 'group' ? 'Batch' : 'Pack'}</div>
                      <div style={{ fontSize:12, color:'var(--ink3)', marginTop:2 }}>
                        {activePack.sessions_total - activePack.sessions_used} of {activePack.sessions_total} sessions remaining · Expires {new Date(activePack.expiry_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                      </div>
                    </div>
                  </div>
              )}

                {packsLoading ? (
                  <p style={{ fontSize:13, color:'var(--ink4)' }}>Loading packs…</p>
                ) : (
                  <>
                    {/* 1:1 SESSIONS */}
                  {packs.some(p => p.type !== 'group') && (
                    <div style={{ marginBottom:32 }}>
                      <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8 }}>
                        <h4 style={{ fontWeight:700, fontSize:20, margin:0 }}>👤 1:1 Sessions</h4>
                        <span style={{ fontSize:12, padding:'3px 12px', borderRadius:6, background:'#E3F4F2', color:'#0B6E65', fontWeight:600 }}>Flexible Schedule</span>
                      </div>
                      <p style={{ fontSize:14, color:'var(--ink3)', marginBottom:16, lineHeight:1.5 }}>Learn at your own pace — book sessions whenever you're ready. Purchase upfront and pick from available slots anytime within the validity period.</p>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
                        {packs.filter(p => p.type !== 'group').map(p => {
                          const perSession = Math.round(p.price / p.session_count);
                          const isOwned = activePack?.pack_id === p.id;
                          return (
                            <div key={p.id} className="card" style={{ borderRadius:10, overflow:'hidden', border: isOwned?'2px solid var(--teal)':'1px solid var(--border2)' }}>
                              {/* Header */}
                              <div style={{ background:'#f0f9f8', padding:'14px 16px', borderBottom:'1px solid var(--border2)', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                                <div>
                                  <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{p.title}</div>
                                  <span style={{ fontSize:11, padding:'3px 10px', borderRadius:6, background:'#E3F4F2', color:'#0B6E65', fontWeight:700 }}>Flexible</span>
                                </div>
                                <div style={{ textAlign:'right' }}>
                                  <div style={{ fontWeight:800, fontSize:18, color:'var(--saffron)', marginBottom:2 }}>₹{Number(p.price).toLocaleString()}</div>
                                  <div style={{ fontSize:10, color:'var(--ink4)' }}>≈ ₹{perSession}/session</div>
                                </div>
                              </div>

                              {/* Info Grid */}
                              <div style={{ padding:'14px 16px', flex:1, display:'flex', flexDirection:'column' }}>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px', marginBottom:14, fontSize:12, color:'var(--ink2)' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                    <span style={{ fontSize:16 }}>📚</span>
                                    <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Sessions</div><div style={{ fontWeight:700 }}>{p.session_count}</div></div>
                                  </div>
                                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                    <span style={{ fontSize:16 }}>⏳</span>
                                    <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Valid For</div><div style={{ fontWeight:700 }}>{p.expiry_days} days</div></div>
                                  </div>
                                </div>

                                {p.description && <p style={{ fontSize:12, color:'var(--ink3)', marginBottom:12, lineHeight:1.4 }}>{p.description}</p>}

                                <button
                                  onClick={() => { setSelectedPack(p); setShowPackBuyModal(true); }}
                                  disabled={isOwned}
                                  className={`btn w-full ${isOwned ? 'btn-teal' : 'btn-primary'}`}
                                  style={{ justifyContent:'center', fontSize:13, padding:'11px', marginTop:'auto' }}>
                                  {isOwned ? '✓ Pack Active — Book from Calendar' : `Buy Pack — ₹${Number(p.price).toLocaleString()} →`}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* GROUP BATCHES */}
                  {packs.some(p => p.type === 'group') && (
                    <div>
                      <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8 }}>
                        <h4 style={{ fontWeight:700, fontSize:20, margin:0 }}>🎓 Group Batches</h4>
                        <span style={{ fontSize:12, padding:'3px 12px', borderRadius:6, background:'#FDF1E3', color:'#D4720C', fontWeight:600 }}>Fixed Schedule</span>
                      </div>
                      <p style={{ fontSize:14, color:'var(--ink3)', marginBottom:16, lineHeight:1.5 }}>Learn with peers in a structured cohort. Enroll in scheduled group classes at specific times and dates. Perfect for community-based learning and accountability.</p>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
                        {packs.filter(p => p.type === 'group').map(p => {
                          const perSession = Math.round(p.price / p.session_count);
                          const isOwned = activePack?.pack_id === p.id;
                          return (
                            <div key={p.id} className="card" style={{ borderRadius:10, overflow:'hidden', border: isOwned?'2px solid #D4720C':'1px solid var(--border2)' }}>
                              {/* Header */}
                              <div style={{ background:'#FFFBF5', padding:'14px 16px', borderBottom:'1px solid var(--border2)', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                                <div>
                                  <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{p.title}</div>
                                  <span style={{ fontSize:11, padding:'3px 10px', borderRadius:6, background:'#FDF1E3', color:'#D4720C', fontWeight:700 }}>Cohort-Based</span>
                                </div>
                                <div style={{ textAlign:'right' }}>
                                  <div style={{ fontWeight:800, fontSize:18, color:'var(--saffron)', marginBottom:2 }}>₹{Number(p.price).toLocaleString()}</div>
                                  <div style={{ fontSize:10, color:'var(--ink4)' }}>≈ ₹{perSession}/session</div>
                                </div>
                              </div>

                              {/* Info Grid */}
                              <div style={{ padding:'14px 16px', flex:1, display:'flex', flexDirection:'column' }}>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px', marginBottom:14, fontSize:12, color:'var(--ink2)' }}>
                                  {p.start_date && (
                                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                      <span style={{ fontSize:16 }}>📅</span>
                                      <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Starts</div><div style={{ fontWeight:700 }}>{new Date(p.start_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}</div></div>
                                    </div>
                                  )}
                                  {p.start_date && (
                                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                      <span style={{ fontSize:16 }}>🏁</span>
                                      <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Ends</div><div style={{ fontWeight:700 }}>{new Date(new Date(p.start_date).getTime() + ((Math.ceil(p.session_count / (p.days_of_week?.length || 1)) * 7)) * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}</div></div>
                                    </div>
                                  )}
                                  {p.days_of_week && p.days_of_week.length > 0 && (
                                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                      <span style={{ fontSize:16 }}>📆</span>
                                      <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Every</div><div style={{ fontWeight:700 }}>{p.days_of_week.join(', ')}</div></div>
                                    </div>
                                  )}
                                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                    <span style={{ fontSize:16 }}>⏱️</span>
                                    <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Time</div><div style={{ fontWeight:700 }}>10:00–11:00</div></div>
                                  </div>
                                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                    <span style={{ fontSize:16 }}>📚</span>
                                    <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Sessions</div><div style={{ fontWeight:700 }}>{p.session_count}</div></div>
                                  </div>
                                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                    <span style={{ fontSize:16 }}>👥</span>
                                    <div><div style={{ fontSize:10, color:'var(--ink4)' }}>Peers</div><div style={{ fontWeight:700 }}>{p.min_learners}–{p.max_learners}</div></div>
                                  </div>
                                </div>

                                {p.description && <p style={{ fontSize:12, color:'var(--ink3)', marginBottom:12, lineHeight:1.4 }}>{p.description}</p>}

                                <button
                                  onClick={() => { setSelectedPack(p); setShowPackBuyModal(true); }}
                                  disabled={isOwned}
                                  className={`btn w-full ${isOwned ? 'btn-orange' : 'btn-primary'}`}
                                  style={{ justifyContent:'center', fontSize:13, padding:'11px', marginTop:'auto', background: isOwned ? '#D4720C' : undefined, color: isOwned ? 'white' : undefined }}>
                                  {isOwned ? '✓ Enrolled — Check Calendar' : `Join Batch — ₹${Number(p.price).toLocaleString()} →`}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    )}
                  </>
                )}
              </div>
            )}
            </div>

          {/* ── Right column: About + Credentials ── */}
          {!isMobile && (<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Intro Video */}
            {expert.intro_link && (
              <div className="card" style={{ padding: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Intro Video</div>
                {getYouTubeId(expert.intro_link) ? (
                  <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setShowIntroModal(true)}>
                    <img
                      src={`https://img.youtube.com/vi/${getYouTubeId(expert.intro_link)}/mqdefault.jpg`}
                      alt="Intro video thumbnail"
                      style={{ width: '100%', display: 'block', borderRadius: 10 }}
                    />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: 10 }}>
                      <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
                        <Play size={20} style={{ color: 'var(--teal)', marginLeft: 3 }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <a href={expert.intro_link} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ gap: 6, fontSize: 13 }}>
                    <Play size={14} /> Watch Intro
                  </a>
                )}
              </div>
            )}

            {/* About */}
            <div className="card" style={{ padding: 20 }} id="expert-about">
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>About</div>
              {expert.bio
                ? <p style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.7, margin: 0 }}>{expert.bio}</p>
                : <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, fontStyle: 'italic' }}>No bio provided yet.</p>
              }
              {expert.experience && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>🏅</span>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Experience</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink1)' }}>{expert.experience} years</div>
                  </div>
                </div>
              )}
              {expert.linkedin_url && (
                <a href={expert.linkedin_url} target="_blank" rel="noreferrer" style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#0A66C2', fontWeight: 600, textDecoration: 'none' }}>
                  <Linkedin size={14} /> LinkedIn Profile
                </a>
              )}
            </div>

            {/* Credentials & Certifications */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--saffron)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Credentials &amp; Certifications</div>
              {expert.credentials
                ? <p style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{expert.credentials}</p>
                : <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, fontStyle: 'italic' }}>No credentials listed yet.</p>
              }
            </div>

            {/* Languages */}
            {expert.languages && (
              <div className="card" style={{ padding: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Languages</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {expert.languages.split(',').map(l => l.trim()).filter(Boolean).map(lang => (
                    <span key={lang} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'var(--bg2)', color: 'var(--ink2)' }}>{lang}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {(expert.tags || []).length > 0 && (
              <div className="card" style={{ padding: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Specialisations</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {expert.tags.map(t => (
                    <span key={t} className="badge badge-teal" style={{ fontSize: 11, padding: '3px 8px' }}>{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>)}

        </div>
      </div>

      {/* About Popup (mobile) */}
      <Modal open={showAboutModal} onClose={() => setShowAboutModal(false)} title="About">
        {expert.bio
          ? <p style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.7, margin: 0 }}>{expert.bio}</p>
          : <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, fontStyle: 'italic' }}>No bio provided yet.</p>
        }
        {expert.experience && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg2)', borderRadius: 10 }}>
            <span style={{ fontSize: 22 }}>🏅</span>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Experience</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink1)' }}>{expert.experience} years</div>
            </div>
          </div>
        )}
        {(expert.linkedin_url || expert.intro_link) && (
          <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {expert.linkedin_url && (
              <a href={expert.linkedin_url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, color: '#0A66C2', background: '#EBF3FB', textDecoration: 'none' }}>
                <Linkedin size={15} /> View LinkedIn Profile
              </a>
            )}
            {expert.intro_link && (
              <button onClick={() => { setShowAboutModal(false); setShowIntroModal(true); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, color: 'var(--teal)', background: '#E3F4F2', border: 'none', cursor: 'pointer' }}>
                <Play size={15} /> Watch Intro
              </button>
            )}
          </div>
        )}
      </Modal>

      {/* Credentials Popup (mobile) */}
      <Modal open={showCredsModal} onClose={() => setShowCredsModal(false)} title="Credentials & Certifications">
        {expert.credentials
          ? <p style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{expert.credentials}</p>
          : <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, fontStyle: 'italic' }}>No credentials listed yet.</p>
        }
      </Modal>

      {/* Languages Popup (mobile) */}
      <Modal open={showLangsModal} onClose={() => setShowLangsModal(false)} title="Languages">
        {expert.languages
          ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {expert.languages.split(',').map(l => l.trim()).filter(Boolean).map(lang => (
                <span key={lang} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, background: 'var(--bg2)', color: 'var(--ink1)', border: '1.5px solid var(--border2)' }}>{lang}</span>
              ))}
            </div>
          : <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, fontStyle: 'italic' }}>No languages listed yet.</p>
        }
      </Modal>

      {/* Intro Video Modal */}
      {expert?.intro_link && (
        <Modal open={showIntroModal} onClose={() => setShowIntroModal(false)} title={`${expert.name}'s Intro`}>
          {getYouTubeId(expert.intro_link) ? (
            <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 10, overflow: 'hidden' }}>
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(expert.intro_link)}?autoplay=1`}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Expert Intro Video"
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Globe size={36} style={{ color: 'var(--teal)', marginBottom: 12 }} />
              <p style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 16 }}>This intro video opens in a new tab.</p>
              <a href={expert.intro_link} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ gap: 6 }}>
                <Play size={14} /> Watch Intro
              </a>
            </div>
          )}
        </Modal>
      )}

      {/* Date Slots Popup */}
      <Modal
        open={showDateSlots}
        onClose={() => { setShowDateSlots(false); setSelectedSlot(null); }}
        title="Slots"
        subtitle={selectedDate
          ? `${calDate.getFullYear()}-${String(calDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate).padStart(2,'0')}`
          : ''}
      >
        {selectedDate && (() => {
          const daySlots = slotsForDay(selectedDate);
          if (daySlots.length === 0) {
            return <p style={{ fontSize: 13, color: 'var(--ink4)', textAlign: 'center', padding: '24px 0' }}>No slots on this date.</p>;
          }
          return (
            <div>
              {daySlots.map(s => {
                const isBooked = !!s.booked;
                return (
                <div key={s.id}
                  className="slot-row"
                  style={{
                    marginBottom: 10, padding: '12px 14px', borderRadius: 10,
                    cursor: !isBooked && canBook ? 'pointer' : 'default',
                    border: `1.5px solid ${isBooked ? 'var(--border2)' : selectedSlot?.id === s.id ? 'var(--teal)' : 'var(--border2)'}`,
                    background: isBooked ? 'var(--bg2)' : selectedSlot?.id === s.id ? '#E3F4F2' : 'var(--surface)',
                    opacity: isBooked ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
                  }}
                  onClick={() => !isBooked && canBook && setSelectedSlot(prev => prev?.id === s.id ? null : s)}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: isBooked ? 'var(--ink3)' : 'var(--ink1)' }}>{s.start} – {s.end}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 2 }}>{s.service}</div>
                  </div>
                  <div className="slot-row-right" style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: isBooked ? 'var(--ink4)' : 'var(--saffron)', fontSize: 15 }}>₹{Number(s.price).toLocaleString()}</div>
                    {isBooked ? (
                      <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 700, color: 'var(--ink4)', background: 'var(--border2)', borderRadius: 6, padding: '3px 10px' }}>Booked</span>
                    ) : canBook ? (
                      <button
                        onClick={e => { handleBookClick(e, s); setShowDateSlots(false); }}
                        disabled={checkingSlotId === s.id}
                        className="btn btn-primary btn-sm"
                        style={{ marginTop: 6, padding: '5px 14px', fontSize: 12 }}>
                        {checkingSlotId === s.id ? '…' : 'Book'}
                      </button>
                    ) : !user ? (
                      <button
                        onClick={e => { e.stopPropagation(); navigate('/auth?mode=login'); }}
                        className="btn btn-secondary btn-sm"
                        style={{ marginTop: 6, padding: '5px 14px', fontSize: 12 }}>
                        Login to Book
                      </button>
                    ) : null}
                  </div>
                </div>
                );
              })}
            </div>
          );
        })()}
      </Modal>

      {/* Booking Modal */}
      <Modal open={showBooking && paymentStep === 'idle'} onClose={() => setShowBooking(false)} title="Confirm Booking" subtitle="Review your session details before booking">
        <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, lineHeight: 2, wordBreak: 'break-word' }}>
          <div><strong>Expert:</strong> {expert.name}</div>
          <div><strong>Service:</strong> {selectedSlot?.service || 'Session'}</div>
          <div><strong>Date:</strong> {selectedSlot?.date || '—'} · {selectedSlot?.start}–{selectedSlot?.end}</div>
          <div><strong>Mode:</strong> Online (LiveKit video call)</div>
        </div>
        <div className="form-group">
          <label className="form-label">Message to tutor (optional)</label>
          <textarea className="form-textarea" placeholder="Tell the tutor about your goals…" value={bookingMsg} onChange={e => setBookingMsg(e.target.value)} />
        </div>
        {activePack && activePack.sessions_used < activePack.sessions_total ? (
          <div style={{ background:'#E3F4F2', border:'1.5px solid var(--teal)', borderRadius:10, padding:'12px 14px', marginBottom:16 }}>
            <div style={{ fontWeight:700, color:'var(--teal)', fontSize:13, marginBottom:2 }}>✅ Pack Credit Will Be Used</div>
            <div style={{ fontSize:12, color:'var(--ink3)' }}>
              {activePack.title} · {activePack.sessions_total - activePack.sessions_used - 1} sessions remaining after this
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--teal)', marginTop:6 }}>Payment due: ₹0</div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14, background: 'var(--saffron-lt)', borderRadius: 10, marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>Total</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--saffron)' }}>₹{Number(selectedSlot?.price || expert.rate || 0).toLocaleString()}</span>
          </div>
        )}
        <div className="modal-cta">
          <button onClick={handleBook} disabled={bookingLoading} className="btn btn-primary w-full" style={{ justifyContent: 'center', padding: 13, fontSize: 15 }}>
            {bookingLoading ? 'Processing…' : activePack && activePack.sessions_used < activePack.sessions_total ? 'Confirm Booking (Pack Credit) →' : 'Proceed to Payment →'}
          </button>
        </div>
      </Modal>

      {/* Cashfree redirect overlay */}
      {paymentStep === 'ordering' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ padding: 32, textAlign: 'center', maxWidth: 320 }}>
            <div style={{ marginBottom: 16, fontSize: 32 }}>💳</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Opening Payment…</div>
            <div style={{ fontSize: 13, color: 'var(--ink3)' }}>You will be redirected to Cashfree's secure payment page.</div>
          </div>
        </div>
      )}

      {/* Course Enrollment Modal */}
      <Modal open={showCourseModal} onClose={() => setShowCourseModal(false)} title="Course Details & Enrollment" subtitle="Review the full schedule before enrolling">
        {selectedCourse && (() => {
          const c = selectedCourse;
          const daysStr = [...(c.days_of_week||[])].sort((a,b)=>a-b).map(d=>DAY_NAMES[d]).join(' & ');
          const isEnrolled = enrolledCourseIds.includes(c.id);
          const perSession = Math.round(c.price / c.total_sessions);
          return (
            <>
              <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{c.title}</div>
                {c.description && <p style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 10 }}>{c.description}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 13 }}>
                  <div>📅 Starts: <strong>{String(c.start_date||'').split('T')[0]}</strong></div>
                  <div>⏰ Time: <strong>{c.time_start?.slice(0,5)}–{c.time_end?.slice(0,5)}</strong></div>
                  <div>🔁 Days: <strong>{daysStr}</strong></div>
                  <div>📚 Sessions: <strong>{c.total_sessions}</strong></div>
                  {c.type === 'group' && <div>👥 Enrolled: <strong>{c.enrolled_count}/{c.max_learners}</strong></div>}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--ink2)' }}>Full Session Schedule</div>
                <div style={{ maxHeight: 200, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
                  {(c.sessions || []).map((s, i) => (
                    <div key={s.id} style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '6px 8px', fontSize: 11 }}>
                      <span style={{ color: 'var(--ink4)', marginRight: 4 }}>#{i+1}</span>
                      <strong>{String(s.session_date||'').split('T')[0]}</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: 'var(--saffron-lt)', border: '1px solid var(--saffron)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Total Payment</div>
                  <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{c.total_sessions} sessions · ≈ ₹{perSession}/session</div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 24, color: 'var(--saffron)' }}>₹{Number(c.price).toLocaleString()}</div>
              </div>

              {isEnrolled ? (
                <div className="btn btn-teal w-full" style={{ justifyContent:'center', padding:12, pointerEvents:'none' }}>✓ You are enrolled in this course</div>
              ) : (
                <button onClick={handleEnrollCourse} disabled={enrollingCourse} className="btn btn-primary w-full" style={{ justifyContent: 'center', padding: 13, fontSize: 15 }}>
                  {enrollingCourse ? 'Processing…' : `Pay ₹${Number(c.price).toLocaleString()} & Enroll →`}
                </button>
              )}
            </>
          );
        })()}
      </Modal>

      {/* ── Buy Pack Modal ── */}
      <Modal open={showPackBuyModal} onClose={() => setShowPackBuyModal(false)} title={selectedPack?.type === 'group' ? 'Join Group Batch' : 'Buy Session Pack'} subtitle={selectedPack?.type === 'group' ? 'Enroll in a scheduled batch program' : 'Pay once — book sessions from the calendar anytime'}>
        {selectedPack && (
          <>
            <div style={{ background:'var(--bg2)', borderRadius:10, padding:16, marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>{selectedPack.title}</div>
              {selectedPack.description && <p style={{ fontSize:13, color:'var(--ink3)', marginBottom:12 }}>{selectedPack.description}</p>}

              {selectedPack.type === 'group' ? (
                <div style={{ display:'flex', flexDirection:'column', gap:6, fontSize:13, lineHeight:1.6 }}>
                  <div>📅 <strong>Starts:</strong> {new Date(selectedPack.start_date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</div>
                  <div>📆 <strong>Every:</strong> {selectedPack.days_of_week?.join(', ')}</div>
                  <div>📚 <strong>Sessions:</strong> {selectedPack.session_count}</div>
                  <div>💡 <strong>Per session:</strong> ₹{Math.round(selectedPack.price/selectedPack.session_count).toLocaleString()}</div>
                  <div>👤 <strong>Expert:</strong> {expert.name}</div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6, fontSize:13, lineHeight:1.6 }}>
                  <div>📚 <strong>Sessions:</strong> {selectedPack.session_count}</div>
                  <div>⏳ <strong>Validity:</strong> {selectedPack.expiry_days} days</div>
                  <div>💡 <strong>Per session:</strong> ₹{Math.round(selectedPack.price/selectedPack.session_count).toLocaleString()}</div>
                  <div>👤 <strong>Expert:</strong> {expert.name}</div>
                </div>
              )}
            </div>

            <div style={{ background: selectedPack.type === 'group' ? '#E3F4F2' : '#fff8e7', border: selectedPack.type === 'group' ? '1px solid var(--teal)' : '1px solid #f59e0b', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13 }}>
              <div style={{ fontWeight:700, marginBottom:6, color: selectedPack.type === 'group' ? 'var(--teal)' : '#b45309' }}>After {selectedPack.type === 'group' ? 'joining this batch' : 'buying this pack'}:</div>
              <div style={{ color:'var(--ink2)', lineHeight:1.8 }}>
                {selectedPack.type === 'group' ? (
                  <>
                    ✓ Access {selectedPack.session_count} scheduled sessions<br />
                    ✓ Learn with {selectedPack.min_learners}–{selectedPack.max_learners} peers<br />
                    ✓ Fixed schedule: {selectedPack.days_of_week?.join(', ')}<br />
                    ✓ All {selectedPack.session_count} sessions included in price
                  </>
                ) : (
                  <>
                    ✓ Pick any {selectedPack.session_count} available slots from the calendar<br />
                    ✓ No additional payment per session<br />
                    ✓ Use all sessions within {selectedPack.expiry_days} days of purchase
                  </>
                )}
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:14, background:'var(--saffron-lt)', borderRadius:10, marginBottom:16 }}>
              <span style={{ fontWeight:600 }}>Total (one-time)</span>
              <span style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, color:'var(--saffron)' }}>₹{Number(selectedPack.price).toLocaleString()}</span>
            </div>

            <div className="modal-cta">
              <button onClick={handleBuyPack} disabled={buyingPack} className="btn btn-primary w-full" style={{ justifyContent:'center', padding:13, fontSize:15 }}>
                {buyingPack ? 'Processing…' : `Pay ₹${Number(selectedPack.price).toLocaleString()} →`}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
